import { useCallback, useEffect, useState } from "react";
import api, { dayName } from "@/lib/api";
import { Spinner, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ChevronLeft, ChevronRight, Sunrise, Sunset, Radio, Users, GraduationCap, Gauge, CalendarDays } from "lucide-react";

// Local (not UTC) date formatting — toISOString() shifts the day for UTC+8 users
const toYMD = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const KIND_LABELS = {
  class: "Class",
  trial: "Trial class",
  replacement: "Replacement",
  adhoc: "Ad-hoc booking",
  reservation: "Studio Reservation",
  occupancy: "Studio Reservation",  // legacy fallback if any cached payload comes through
};

const START = 600; // 10:00
const SPAN = 720; // 12h -> 22:00
const mins = (t) => {
  const [h, m] = t.split(":");
  return Number(h) * 60 + Number(m);
};
const pos = (t) => Math.min(100, Math.max(0, ((mins(t) - START) / SPAN) * 100));
const HOURS = Array.from({ length: 13 }, (_, i) => 10 + i);

const KIND_STYLES = {
  class: "bg-primary/85 text-primary-foreground",
  trial: "bg-[#1D4ED8] text-white",
  replacement: "bg-[#B8860B] text-white",
  adhoc: "bg-[#B8860B] text-white",
  // Studio Reservation: amber outline (room held, no real lesson)
  reservation: "bg-amber-100 text-amber-900 border border-amber-400 border-dashed",
  occupancy: "bg-amber-100 text-amber-900 border border-amber-400 border-dashed",
};

const Mini = ({ icon: Icon, label, value, sub, live, testId }) => (
  <div className="rounded-lg border bg-card px-4 py-3 flex items-center gap-3" data-testid={testId}>
    <div className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${live ? "bg-[#D8F3DC]" : "bg-muted"}`}>
      <Icon className={`h-4 w-4 ${live ? "text-[#2D6A4F]" : "text-secondary"}`} />
    </div>
    <div className="min-w-0">
      <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">{label}</p>
      <p className="text-lg font-bold font-display leading-tight flex items-center gap-2">
        {value}
        {live && Number(String(value).replace(/\D/g, "")) > 0 && (
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2D6A4F] opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#2D6A4F]" />
          </span>
        )}
      </p>
      {sub && <p className="text-[11px] text-muted-foreground truncate">{sub}</p>}
    </div>
  </div>
);

const heat = (pct) =>
  pct >= 60 ? "bg-[#2D6A4F]" : pct >= 35 ? "bg-[#74A57F]" : pct > 0 ? "bg-[#B7CFB7]" : "bg-muted-foreground/20";

const BlockTip = ({ e }) => (
  <div className="space-y-1.5 max-w-[280px]" data-testid={`glance-tip-${e.id}`}>
    <p className="font-semibold">
      {e.start_time}–{e.end_time} · {KIND_LABELS[e.kind] || e.kind}
    </p>
    {e.on_teacher_leave && (
      <p className="text-destructive font-medium">
        Teacher on {e.teacher_leave?.category === "mc" ? "Medical Leave"
          : e.teacher_leave?.category === "emergency" ? "Emergency Leave" : "Leave"} — awaiting replacement
      </p>
    )}
    {e.teacher_name && <p>Teacher: {e.teacher_name}</p>}
    {e.kind === "occupancy" && e.course_name && <p>Instruments: {e.course_name}</p>}
    {e.students?.length > 0 ? (
      <div className="pt-1.5 border-t border-border/40">
        <p className="text-[10px] uppercase tracking-wider opacity-70 mb-1">
          Students ({e.students.length})
        </p>
        {e.students.map((s, i) => (
          <p key={i}>
            {s.start_time}–{s.end_time} · {s.student_name || "—"}
            {s.course_name ? ` · ${s.course_name}` : ""}
          </p>
        ))}
      </div>
    ) : (e.kind === "occupancy" || e.kind === "reservation") ? (
      <p className="opacity-70 italic">{e.students?.length ? "" : "Room reserved — no enrolled student yet"}</p>
    ) : (
      e.student_name && <p>{e.student_name}{e.course_name ? ` · ${e.course_name}` : ""}</p>
    )}
  </div>
);

export default function TodayGlance() {
  const [date, setDate] = useState(""); // "" = today (server default)
  const [showReservations, setShowReservations] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    const params = { include_reservations: showReservations ? "true" : "false" };
    if (date) params.date = date;
    api
      .get("/dashboard/today-at-glance", { params })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [date, showReservations]);

  useEffect(() => {
    setLoading(true);
    load();
    const t = setInterval(load, 60000); // live refresh every minute
    return () => clearInterval(t);
  }, [load]);

  const shift = (d) => {
    const base = new Date((date || data?.date || toYMD(new Date())) + "T00:00:00");
    base.setDate(base.getDate() + d);
    setDate(toYMD(base));
  };

  if (loading && !data) return <div className="py-10 flex justify-center"><Spinner /></div>;
  if (!data) return <EmptyState text="Could not load today's overview." />;

  const s = data.summary;
  const isToday = !!data.now;
  const nowPos = isToday && mins(data.now) >= START && mins(data.now) <= START + SPAN ? pos(data.now) : null;

  return (
    <TooltipProvider>
    <div data-testid="today-glance" className="mb-10">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-xl font-semibold">Today @ a Glance</h3>
          {isToday && (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#2D6A4F] bg-[#D8F3DC] rounded-full px-2.5 py-1" data-testid="live-clock">
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2D6A4F] opacity-60" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#2D6A4F]" />
              </span>
              LIVE · {data.now}
            </span>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 lg:pr-16">
          {/* Today row sits ABOVE the date row so the right-arrow can be tapped
              repeatedly without accidentally hitting Today next to it. */}
          {date && (
            <Button variant="outline" size="sm" className="h-8" onClick={() => setDate("")} data-testid="glance-today-btn">
              <CalendarDays className="h-3.5 w-3.5 mr-1" />Today
            </Button>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shift(-1)} data-testid="glance-prev-day">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 min-w-[180px] font-medium" data-testid="glance-date">
                  <CalendarDays className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
                  {dayName(data.day_of_week)}, {data.date}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end" data-testid="glance-date-picker">
                <Calendar mode="single" selected={new Date(data.date + "T00:00:00")}
                  defaultMonth={new Date(data.date + "T00:00:00")}
                  onSelect={(d) => d && setDate(toYMD(d))} initialFocus />
              </PopoverContent>
            </Popover>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shift(1)} data-testid="glance-next-day">
              <ChevronRight className="h-4 w-4" />
            </Button>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none ml-2 border-l pl-3"
                   data-testid="show-reservations-toggle">
              <input type="checkbox" checked={showReservations}
                    onChange={(e) => setShowReservations(e.target.checked)}
                    className="rounded" />
            Show Reservations
          </label>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        <Mini icon={Sunrise} label="First class" value={s.first_class || "—"} testId="glance-first-class" />
        <Mini icon={Sunset} label="Last class ends" value={s.last_class || "—"} testId="glance-last-class" />
        <Mini icon={Radio} label="Live now" value={s.live_count} live
          sub={s.live_now?.[0] ? [s.live_now[0].student_name, s.live_now[0].teacher_name].filter(Boolean).join(" · ") : null}
          testId="glance-live-now" />
        <Mini icon={GraduationCap} label="Classes" value={s.total_classes} testId="glance-total-classes" />
        <Mini icon={Users} label="Teachers in" value={s.teachers_today} testId="glance-teachers" />
        <Mini icon={Gauge} label="Occupancy" value={`${s.avg_occupancy}%`} testId="glance-occupancy" />
      </div>

      <div className="rounded-lg border bg-card overflow-x-auto" data-testid="glance-timeline">
        <div className="min-w-[860px]">
          {/* hour ruler */}
          <div className="flex border-b bg-muted/40">
            <div className="w-36 shrink-0 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
              Studio
            </div>
            <div className="relative flex-1 h-8">
              {HOURS.map((h) => (
                <span key={h} className="absolute top-2 -translate-x-1/2 text-[10px] font-mono text-muted-foreground"
                  style={{ left: `${((h * 60 - START) / SPAN) * 100}%` }}>
                  {h}:00
                </span>
              ))}
            </div>
          </div>

          {data.studios.map((st) => (
            <div key={st.id} className="flex border-b last:border-b-0 hover:bg-muted/20" data-testid={`glance-lane-${st.id}`}>
              <div className="w-36 shrink-0 px-3 py-2 flex flex-col justify-center gap-1">
                <span className="text-xs font-semibold truncate flex items-center gap-1.5">
                  {st.name}
                  {st.live && <span className="h-1.5 w-1.5 rounded-full bg-[#2D6A4F] animate-pulse" />}
                </span>
                <div className="flex items-center gap-1.5">
                  <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden">
                    <div className={`h-full rounded-full ${heat(st.occupancy_pct)}`} style={{ width: `${Math.min(100, st.occupancy_pct)}%` }} />
                  </div>
                  <span className="text-[10px] font-mono text-muted-foreground">{st.occupancy_pct}%</span>
                </div>
              </div>
              <div className="relative flex-1 h-14">
                {HOURS.map((h) => (
                  <div key={h} className="absolute top-0 bottom-0 border-l border-border/50"
                    style={{ left: `${((h * 60 - START) / SPAN) * 100}%` }} />
                ))}
                {st.events.map((e) => (
                  <Tooltip key={e.id} delayDuration={150}>
                    <TooltipTrigger asChild>
                      <div
                        data-testid={`glance-block-${e.id}`}
                        className={`absolute top-1.5 bottom-1.5 rounded-md px-1.5 py-0.5 overflow-hidden text-[10px] leading-tight cursor-default
                          ${e.on_teacher_leave
                            ? "bg-neutral-300 text-neutral-500 ring-1 ring-destructive/50 line-through decoration-1"
                            : (KIND_STYLES[e.kind] || KIND_STYLES.class)}
                          ${e.live ? "ring-2 ring-[#2D6A4F] ring-offset-1 shadow-md" : ""}
                          ${e.done ? "opacity-45" : ""}`}
                        style={{ left: `${pos(e.start_time)}%`, width: `${Math.max(2.5, pos(e.end_time) - pos(e.start_time))}%` }}>
                        <p className="font-semibold truncate">{e.student_name || e.teacher_name || e.kind}</p>
                        <p className="truncate opacity-80">
                          {e.start_time} {e.kind === "occupancy" ? (e.course_name && `· ${e.course_name}`) : (e.teacher_name && `· ${e.teacher_name.split(" ")[0]}`)}
                        </p>
                        {e.on_teacher_leave && (
                          <span className="absolute top-0 right-0 text-[8px] font-bold bg-destructive text-destructive-foreground px-1 rounded-bl"
                                data-testid={`glance-block-${e.id}-leave-badge`}>
                            {e.teacher_leave?.category === "mc" ? "MC"
                              : e.teacher_leave?.category === "emergency" ? "EL" : "LEAVE"}
                          </span>
                        )}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs">
                      <BlockTip e={e} />
                    </TooltipContent>
                  </Tooltip>
                ))}
                {nowPos !== null && (
                  <div className="absolute top-0 bottom-0 w-px bg-[#D90429] z-10" style={{ left: `${nowPos}%` }}>
                    <div className="absolute -top-0 -translate-x-1/2 h-1.5 w-1.5 rounded-full bg-[#D90429]" />
                  </div>
                )}
              </div>
            </div>
          ))}
          {data.studios.length === 0 && <div className="p-6"><EmptyState text="No studios configured." /></div>}
        </div>
      </div>

      {data.unassigned.length > 0 && (
        <div className="mt-3 rounded-lg border border-dashed bg-card px-4 py-3" data-testid="glance-unassigned">
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-2">
            No studio assigned ({data.unassigned.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {data.unassigned.map((e) => (
              <span key={e.id} className={`text-[11px] rounded-full px-2.5 py-1 bg-muted font-medium ${e.done ? "opacity-50" : ""}`}>
                {e.start_time}–{e.end_time} · {e.student_name || e.kind} {e.teacher_name && `(${e.teacher_name})`}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4 mt-2 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-primary/85" />Class</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-[#1D4ED8]" />Trial</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-[#B8860B]" />Replacement / Ad-hoc</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-neutral-300 ring-1 ring-destructive/60" />Teacher on leave</span>
        {showReservations && (
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-amber-100 border border-dashed border-amber-400" />Studio Reservation</span>
        )}
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm ring-2 ring-[#2D6A4F] bg-primary/85" />Live now</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-px bg-[#D90429] inline-block" />Current time</span>
      </div>
    </div>
    </TooltipProvider>
  );
}
